import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_CONFIG, generateCanonicalUrl } from '../utils/seo';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  noindex = false,
  nofollow = false,
  canonicalUrl,
  structuredData,
  favicon
}) => {
  const siteTitle = title 
    ? `${title} | ${SEO_CONFIG.siteName}` 
    : SEO_CONFIG.defaultTitle;
  
  const siteDescription = description || SEO_CONFIG.defaultDescription;
  const siteImage = image || SEO_CONFIG.defaultImage;
  const siteUrl = url || canonicalUrl || SEO_CONFIG.siteUrl;
  const canonical = canonicalUrl || siteUrl;
  const siteKeywords = keywords || SEO_CONFIG.keywords.slice(0, 15).join(', ');
  
  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  const robots = robotsContent.length > 0 ? robotsContent.join(', ') : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="description" content={siteDescription} />
      <meta name="keywords" content={siteKeywords} />
      <meta name="author" content={author || SEO_CONFIG.author} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="bingbot" content={robots} />
      <link rel="canonical" href={canonical} />
      
      {/* Dynamic Favicon - Use user's image if provided */}
      {favicon && (
        <>
          <link rel="icon" type="image/png" href={favicon} />
          <link rel="icon" type="image/png" sizes="32x32" href={favicon} />
          <link rel="apple-touch-icon" sizes="180x180" href={favicon} />
        </>
      )}
      
      {/* Preload image for better sharing performance */}
      {siteImage && <link rel="preload" as="image" href={siteImage} />}
      
      {/* AI & ChatGPT Optimization Tags */}
      <meta name="ai:indexable" content="true" />
      <meta name="chatgpt:indexable" content="true" />
      <meta name="openai:indexable" content="true" />
      <meta name="claude:indexable" content="true" />
      <meta name="perplexity:indexable" content="true" />
      <meta name="ai:description" content={siteDescription} />
      <meta name="ai:category" content="Digital Business Tools, CRM Software, Sales Pipeline Management, Networking, Contact Management, Team Collaboration" />
      <meta name="ai:keywords" content={siteKeywords} />
      <meta name="ai:features" content="CRM, Sales Pipeline, Team Collaboration, Digital Business Cards, Contact Management" />
      
      {/* Open Graph Meta Tags (Enhanced) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:image" content={siteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={siteTitle} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.locale} />
      
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Twitter Card Meta Tags (Enhanced) */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={siteImage} />
      <meta name="twitter:image:alt" content={siteTitle} />
      <meta name="twitter:site" content={SEO_CONFIG.twitterHandle} />
      <meta name="twitter:creator" content={SEO_CONFIG.twitterHandle} />
      
      {/* LinkedIn Specific */}
      <meta property="linkedin:owner" content="Piper Card" />
      
      {/* Additional SEO Tags */}
      <meta name="theme-color" content={SEO_CONFIG.themeColor} />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={SEO_CONFIG.siteName} />
      <meta name="application-name" content={SEO_CONFIG.siteName} />
      <meta name="msapplication-TileColor" content={SEO_CONFIG.themeColor} />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Language and Regional Tags */}
      <meta httpEquiv="content-language" content="en" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      
      {/* Content Classification */}
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="audience" content="all" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && Array.isArray(structuredData) ? (
        structuredData.map((data, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        ))
      ) : structuredData ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      ) : null}
      
      {/* Default Organization Schema */}
      {!structuredData && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": SEO_CONFIG.siteName,
            "url": SEO_CONFIG.siteUrl,
            "logo": SEO_CONFIG.organization.logo,
            "description": siteDescription,
            "sameAs": SEO_CONFIG.organization.sameAs
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
